import { useEffect, useRef, useState } from 'react';

/**
 * O "⋯" DE CADA CONTA GUARDADA (25/10/2026, pedido do pesquisador): "Esquecer" não fica mais ao lado da conta. A lista
 * abria com um botão destrutivo sob o ponteiro — um toque a mais, e a conta sumia do aparelho. Agora: "⋯" abre o menu,
 * "Esquecer esta conta…" abre a pergunta, e a pergunta aparece EMBAIXO da linha ("Esquecer esta conta neste navegador?"),
 * longe de onde o ponteiro estava. Usado pelas três listas: a folha do aplicativo, a do navegador e a tela de entrar.
 *
 * Fica dentro de um `<li>` com `flex-wrap`: a pergunta ocupa a linha de baixo inteira (`basis-full`).
 */
export function MenuDaConta({ userId, nome, onde, desabilitado, onEsquecer, explicacao }: {
  userId: number;
  nome: string;
  /** "neste navegador" ou "neste aparelho". */
  onde: string;
  desabilitado?: boolean;
  onEsquecer: () => void | Promise<void>;
  /** A frase da pergunta, embaixo do título. */
  explicacao?: string;
}) {
  const [estado, setEstado] = useState<'fechado' | 'menu' | 'pergunta'>('fechado');
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (estado !== 'menu') return;
    const fora = (e: PointerEvent) => { if (caixa.current && !caixa.current.contains(e.target as Node)) setEstado('fechado'); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setEstado('fechado'); };
    document.addEventListener('pointerdown', fora);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', fora); document.removeEventListener('keydown', esc); };
  }, [estado]);
  return (
    <>
      <div ref={caixa} className="relative shrink-0">
        <button type="button" disabled={desabilitado} onClick={() => setEstado(estado === 'menu' ? 'fechado' : 'menu')}
          aria-haspopup="menu" aria-expanded={estado === 'menu'} aria-label={`Mais opções de ${nome}`}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-lg leading-none text-gray-600 hover:bg-gray-100" data-testid={`mais-da-conta-${userId}`}>⋯</button>
        {estado === 'menu' && (
          <div role="menu" className="absolute right-0 top-full z-10 mt-1 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            <button type="button" role="menuitem" onClick={() => setEstado('pergunta')} className="flex min-h-[44px] w-full items-center px-3 text-left text-sm text-gray-800 hover:bg-gray-50" data-testid={`esquecer-${userId}`}>
              Esquecer esta conta…
            </button>
          </div>
        )}
      </div>
      {estado === 'pergunta' && (
        <div className="basis-full rounded-md border border-red-200 bg-red-50 p-3 text-sm text-gray-800" role="alertdialog" aria-label={`Esquecer ${nome} ${onde}?`} data-testid={`confirmar-remover-${userId}`}>
          <p className="font-medium">Esquecer esta conta {onde}?</p>
          {explicacao && <p className="mt-1 text-gray-700">{explicacao}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setEstado('fechado')} className="min-h-[44px] rounded-md border border-gray-300 bg-white px-3">Cancelar</button>
            <button type="button" onClick={() => { setEstado('fechado'); void onEsquecer(); }} className="min-h-[44px] rounded-md bg-red-600 px-3 text-white" data-testid={`confirmar-esquecer-${userId}`}>Esquecer</button>
          </div>
        </div>
      )}
    </>
  );
}
